import { useSyncExternalStore } from 'react';
import { clamp, priceY, timeX } from './chart-data';
import type { Anchor, Candle, Scale } from './chart-data';
import type { Timeframe } from './zone-math';
import { INSTRUMENTS, formatPrice } from './instruments';
import type { Symbol } from './instruments';

type Measurement = { start: Anchor; end: Anchor } | null;
export function createMeasure() {
  let value: Measurement = null;
  const listeners = new Set<() => void>();
  return { read: () => value, subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; }, set: (next: Measurement) => { value = next; listeners.forEach(fn => fn()); } };
}
export function measureValues(start: Anchor, end: Anchor, rows: Candle[], pip: number) {
  const change = end.price - start.price;
  return { change, pips: change / pip, percent: start.price > 0 ? change / start.price * 100 : 0,
    bars: Math.abs(rows.findIndex(row => row.time === end.time) - rows.findIndex(row => row.time === start.time)), minutes: Math.round(Math.abs(end.time - start.time) / 60000) };
}
export function ChartMeasure({ store, rows, allRows, scale, left, right, candleRight, timeframe, symbol }: { store: ReturnType<typeof createMeasure>; rows: Candle[]; allRows: Candle[]; scale: Scale; left: number; right: number; candleRight: number; timeframe: Timeframe; symbol: Symbol }) {
  const value = useSyncExternalStore(store.subscribe, store.read, store.read);
  if (!value || !rows.length) return null;
  const { start, end } = value;
  if (!allRows.some(row => row.time === start.time) || !allRows.some(row => row.time === end.time)) return null;
  const x1 = timeX(start.time, rows, left, candleRight, timeframe), x2 = timeX(end.time, rows, left, candleRight, timeframe);
  const y1 = priceY(start.price, scale), y2 = priceY(end.price, scale);
  const info = measureValues(start, end, allRows, INSTRUMENTS[symbol].pipSize);
  const sign = info.change >= 0 ? '+' : '';
  const boxWidth = Math.min(250, right - left - 8), x = clamp((x1 + x2) / 2 - boxWidth / 2, left + 4, right - boxWidth - 4);
  const y = clamp(Math.min(y1, y2) - 86, scale.top + 5, scale.bottom - 82);
  return <g data-testid="chart-measure" pointerEvents="none">
    <path d={`M${x1},${y1} L${x2},${y2} M${x1},${y1} H${x2} V${y2}`} fill="none" stroke="#386b94" strokeWidth="1.3" strokeDasharray="4 4" />
    {[{ x: x1, y: y1 }, { x: x2, y: y2 }].map((p, i) => <path key={i} d={`M${p.x - 5},${p.y}h10 M${p.x},${p.y - 5}v10`} stroke="#386b94" strokeWidth="2" />)}
    <rect x={x} y={y} width={boxWidth} height="76" rx="8" fill="#20323f" stroke="#607b8c" />
    <text x={x + 12} y={y + 16} fontSize="8" fill="#a5c0d3" letterSpacing="1.4">UKUR PERGERAKAN</text>
    <text x={x + 12} y={y + 37} fontSize="17" fill="#fff">{sign}{info.pips.toFixed(1)} pips</text>
    <text x={x + 12} y={y + 54} fontSize="10" fill="#d1e0e9">{sign}{formatPrice(symbol, info.change)} · {sign}{info.percent.toFixed(2)}%</text>
    <text x={x + 12} y={y + 68} fontSize="9" fill="#a5c0d3">{info.bars} candle · {info.minutes >= 60 ? (info.minutes / 60).toFixed(1) + ' jam' : info.minutes + ' min'}</text>
  </g>;
}
